// Push/pull for the `sponsor_tasks` table. Mirrors the check-ins flow but
// additionally maps recurrence (Dexie object → server unit+interval columns)
// and respects `assigned_by`: server-assigned tasks (assigned_by != null)
// were created by a sponsor and pull-only land locally; locally-created
// tasks have `assigned_by = null` and push up as the sponsee's own.
//
// NOTE: unlike checkInsSync / tenthStepsSync, this module intentionally
// SKIPS content-based dedupe at pull time. sponsor_tasks lacks a clean
// natural key — titles can be edited, due dates are optional, and recurring
// tasks legitimately create near-duplicates. Treating two same-titled tasks
// as one would silently lose data. Users can manually delete duplicates from
// the UI; see src/services/sync/dedupe.ts for the full rationale.

import { z } from 'zod';
import { db, withDeleteHookSuppressed, type SponsorTask } from '../db';
import { isSupabaseConfigured, supabase } from '../supabase/client';
import { cursorOrEpoch, writeCursor } from './cursor';
import type { SyncResult } from './types';
import { EMPTY_SYNC_RESULT } from './types';

// See checkInsSync.ts for the rationale. assigned_by is the sponsor's user
// id when the task was created by a sponsor; null for self-created tasks.
const remoteSponsorTaskRowSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  owner_id: z.string(),
  assigned_by: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  is_completed: z.boolean(),
  due_date: z.string().nullable(),
  completed_date: z.string().nullable(),
  recurrence_unit: z.enum(['daily', 'weekly', 'monthly']).nullable(),
  recurrence_interval: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});

type RemoteSponsorTaskRow = z.infer<typeof remoteSponsorTaskRowSchema>;

interface PushPayload {
  client_id: string;
  owner_id: string;
  assigned_by: string | null;
  title: string;
  description: string;
  is_completed: boolean;
  due_date: string | null;
  completed_date: string | null;
  recurrence_unit: 'daily' | 'weekly' | 'monthly' | null;
  recurrence_interval: number | null;
  updated_at: string;
  deleted_at: string | null;
}

export function buildSponsorTaskPushPayload(
  row: SponsorTask,
  ownerId: string,
  now: Date = new Date(),
): PushPayload {
  return {
    client_id: row.clientId ?? crypto.randomUUID(),
    owner_id: ownerId,
    // Local-only tasks have no sponsor attribution; sponsor-assigned tasks
    // came down with assigned_by set and we preserve that on push.
    assigned_by: row.assignedBy ?? null,
    title: row.title,
    description: row.description,
    is_completed: row.isCompleted,
    due_date: row.dueDate ? row.dueDate.toISOString() : null,
    completed_date: row.completedDate ? row.completedDate.toISOString() : null,
    recurrence_unit: row.recurrence?.unit ?? null,
    recurrence_interval: row.recurrence?.interval ?? null,
    updated_at: (row.updatedAt ?? now).toISOString(),
    deleted_at: row.deletedAt ? row.deletedAt.toISOString() : null,
  };
}

export function applyRemoteSponsorTask(
  remote: RemoteSponsorTaskRow,
): Omit<SponsorTask, 'id'> {
  return {
    clientId: remote.client_id,
    title: remote.title,
    description: remote.description ?? '',
    isCompleted: remote.is_completed,
    createdAt: new Date(remote.created_at),
    dueDate: remote.due_date ? new Date(remote.due_date) : undefined,
    completedDate: remote.completed_date
      ? new Date(remote.completed_date)
      : undefined,
    recurrence:
      remote.recurrence_unit && remote.recurrence_interval
        ? {
            unit: remote.recurrence_unit,
            interval: remote.recurrence_interval,
          }
        : undefined,
    assignedBy: remote.assigned_by,
    updatedAt: new Date(remote.updated_at),
    deletedAt: remote.deleted_at ? new Date(remote.deleted_at) : null,
  };
}

export async function syncSponsorTasksUp(userId: string): Promise<SyncResult> {
  if (!isSupabaseConfigured() || !supabase) return { ...EMPTY_SYNC_RESULT };
  const client = supabase;
  const result: SyncResult = { ...EMPTY_SYNC_RESULT, errors: [] };

  const local = await db.sponsorTasks.toArray();
  const withClientIds = local.filter((r) => r.clientId);
  if (withClientIds.length > 0) {
    const payload = withClientIds.map((r) =>
      buildSponsorTaskPushPayload(r, userId),
    );
    const { error } = await client
      .from('sponsor_tasks')
      .upsert(payload, { onConflict: 'owner_id,client_id' });
    if (error) {
      result.errors.push(`sponsor_tasks push: ${error.message}`);
    } else {
      result.pushed += payload.length;
    }
  }

  const tombstones = await db.tombstones
    .where('table')
    .equals('sponsorTasks')
    .toArray();
  if (tombstones.length > 0) {
    const payload = tombstones.map((t) => ({
      client_id: t.clientId,
      owner_id: userId,
      // Title is NOT NULL on the server; supply a sentinel that never
      // round-trips because the row will be filtered out by `deleted_at`.
      title: '(deleted)',
      updated_at: t.deletedAt.toISOString(),
      deleted_at: t.deletedAt.toISOString(),
    }));
    const { error } = await client
      .from('sponsor_tasks')
      .upsert(payload, { onConflict: 'owner_id,client_id' });
    if (error) {
      result.errors.push(`sponsor_tasks tombstone push: ${error.message}`);
    } else {
      result.pushed += payload.length;
      const ids = tombstones
        .map((t) => t.id)
        .filter((id): id is number => id !== undefined);
      if (ids.length > 0) await db.tombstones.bulkDelete(ids);
    }
  }
  return result;
}

export async function syncSponsorTasksDown(
  userId: string,
): Promise<SyncResult> {
  if (!isSupabaseConfigured() || !supabase) return { ...EMPTY_SYNC_RESULT };
  const client = supabase;
  const result: SyncResult = { ...EMPTY_SYNC_RESULT, errors: [] };

  const since = cursorOrEpoch('sponsor_tasks');
  const { data, error } = await client
    .from('sponsor_tasks')
    .select('*')
    .eq('owner_id', userId)
    .gt('updated_at', since)
    .order('updated_at', { ascending: true });
  if (error) {
    result.errors.push(`sponsor_tasks pull: ${error.message}`);
    return result;
  }

  const parsed = z.array(remoteSponsorTaskRowSchema).safeParse(data);
  if (!parsed.success) {
    result.errors.push(
      `sponsor_tasks pull: row schema mismatch — ${parsed.error.message}`,
    );
    return result;
  }
  const rows = parsed.data;
  if (rows.length === 0) return result;

  // Skip remotes shadowed by a pending local tombstone — the push will
  // tombstone the server row on the same sync round. See checkInsSync for
  // the full rationale.
  const pendingTombstoneClientIds: ReadonlySet<string> = new Set(
    (await db.tombstones.where('table').equals('sponsorTasks').toArray()).map(
      (t) => t.clientId,
    ),
  );

  await withDeleteHookSuppressed(async () => {
    await db.transaction('rw', db.sponsorTasks, async () => {
      for (const remote of rows) {
        if (pendingTombstoneClientIds.has(remote.client_id)) {
          continue;
        }
        const local = await db.sponsorTasks
          .where('clientId')
          .equals(remote.client_id)
          .first();

        if (remote.deleted_at) {
          if (local?.id != null) await db.sponsorTasks.delete(local.id);
          result.pulled += 1;
          continue;
        }

        const incomingUpdated = new Date(remote.updated_at).getTime();
        const localUpdated = local?.updatedAt?.getTime() ?? -Infinity;
        if (local && localUpdated >= incomingUpdated) {
          result.conflicts += 1;
          continue;
        }
        const next = applyRemoteSponsorTask(remote);
        if (local?.id != null) {
          await db.sponsorTasks.put({ ...next, id: local.id });
        } else {
          await db.sponsorTasks.add(next);
        }
        result.pulled += 1;
      }
    });
  });

  const last = rows[rows.length - 1];
  if (last) writeCursor('sponsor_tasks', last.updated_at);

  return result;
}
